<template>
  <div
    class="pointer-events-none fixed bottom-4 left-1/2 z-[2000] -translate-x-1/2"
  >
    <transition name="ui-snackbar">
      <div
        v-if="show"
        :class="[
          'pointer-events-auto flex w-fit max-w-[90vw] min-w-[300px] items-center justify-between gap-3 rounded-lg px-4 py-3 text-sm text-white shadow-lg',
          typeClass,
        ]"
        :style="snackbarStyle"
        role="status"
      >
        <slot />
        <button
          v-if="dismissible"
          type="button"
          class="opacity-70 hover:opacity-100"
          aria-label="close"
          @click="close"
        >
          <UiIcon name="x" :size="18" />
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { resolveThemeColor } from './themeColor'
import UiIcon from './UiIcon.vue'

/** UiSnackbar — Vuetify <v-snackbar> replacement (bottom-centered).
 * Dual Vue 2 / Vue 3 compatible. */
const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    value?: boolean
    color?: string
    type?: 'info' | 'success' | 'warning' | 'error' | 'default'
    dismissible?: boolean
  }>(),
  {
    color: undefined,
    type: 'default',
    dismissible: true,
  },
)

const emit = defineEmits<(e: 'update:modelValue', v: boolean) => void>()

const show = computed(() => props.modelValue)

function close() {
  emit('update:modelValue', false)
}

const typeClass = computed(() => {
  if (props.color != null) return ''
  const map: Record<string, string> = {
    info: 'bg-info',
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error',
    default: 'bg-gray-900',
  }
  return map[props.type]
})

/** Inline style applies only when `color` is set; otherwise the typeClass bg-* wins. */
const snackbarStyle = computed(() => {
  const c = resolveThemeColor(props.color)
  return c != null ? { backgroundColor: c } : undefined
})
</script>

<style scoped>
.ui-snackbar-enter-active,
.ui-snackbar-leave-active {
  transition:
    opacity 0.25s,
    transform 0.25s;
}
.ui-snackbar-enter-from,
.ui-snackbar-enter,
.ui-snackbar-leave-to {
  opacity: 0;
  transform: translateY(1rem);
}
</style>
